import { z } from "zod";
import { BLOCKING_CLASSES, BlockingClassSchema, DECISION_AUDIENCES, DecisionAudienceSchema, GatePolicySchema, LEDGER_RESOLUTION_OUTCOMES, LEDGER_STATES, LedgerResolutionOutcomeSchema, LedgerWaiverV1Schema, Sha256Schema } from "./projectLedger.js";
const NonEmptyStringSchema = z.string().trim().min(1);
const TimestampSchema = z.string().datetime({ offset: true });
const CountSchema = z.number().int().nonnegative();
const readinessGatePolicyShape = Object.fromEntries(BLOCKING_CLASSES.map((blockingClass) => [
    blockingClass,
    z
        .object({
        mandatory: z.boolean(),
        policy: GatePolicySchema,
        allowedResolutionOutcomes: z.array(LedgerResolutionOutcomeSchema),
        waiverRoleIds: z.array(NonEmptyStringSchema)
    })
        .strict()
        .superRefine((gate, context) => {
        if (gate.policy === "hard" && (!gate.mandatory || gate.waiverRoleIds.length)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "A hard gate must be mandatory and cannot have waiver roles."
            });
        }
        if (gate.policy === "waivable" &&
            (!gate.mandatory || gate.waiverRoleIds.length === 0)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "A waivable gate must be mandatory with authorised roles."
            });
        }
        if (gate.policy === "advisory" && gate.mandatory) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "An advisory gate cannot be mandatory."
            });
        }
    })
]));
export const ReadinessPolicyProfileV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    id: NonEmptyStringSchema,
    version: NonEmptyStringSchema,
    issueProfile: NonEmptyStringSchema,
    gates: z.object(readinessGatePolicyShape).strict(),
    downstreamRequiredChecksVersion: NonEmptyStringSchema,
    progressWorkUnitsVersion: NonEmptyStringSchema,
    allowBlockedInternalDraft: z.boolean()
})
    .strict()
    .superRefine((profile, context) => {
    const uncertifiedDrafts = profile.gates.uncertified_drafts;
    if (uncertifiedDrafts.policy !== "hard" ||
        !uncertifiedDrafts.mandatory ||
        uncertifiedDrafts.waiverRoleIds.length > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["gates", "uncertified_drafts"],
            message: "Uncertified drafts must remain a mandatory hard gate with no waiver roles."
        });
    }
});
export const ProjectReadinessSourceVersionV1Schema = z
    .object({
    projectRevision: NonEmptyStringSchema,
    readinessPolicyProfileId: NonEmptyStringSchema,
    readinessPolicyVersion: NonEmptyStringSchema,
    specResultId: NonEmptyStringSchema.nullable(),
    generationInputFingerprint: Sha256Schema.nullable(),
    ledgerRevision: z.number().int().nonnegative(),
    catalogueRelease: NonEmptyStringSchema.nullable(),
    anomalyRuleVersion: NonEmptyStringSchema,
    outputValidatorVersion: NonEmptyStringSchema,
    documentWatermark: Sha256Schema,
    costTelemetryWatermark: Sha256Schema,
    sourceVersionHash: Sha256Schema
})
    .strict();
export const DownstreamRowConfidenceV1Schema = z
    .object({
    rowId: NonEmptyStringSchema,
    rowType: z.enum(["product", "mapping", "panel"]),
    completeness: z.enum(["complete", "review", "incomplete", "not_applicable"]),
    confidence: z
        .object({
        label: z.enum(["high", "medium", "low", "unknown"]),
        score: z.number().min(0).max(1).nullable(),
        basis: z.array(z.enum([
            "source_backed",
            "rule_backed",
            "accepted_decision",
            "defaulted",
            "derived",
            "missing"
        ]))
    })
        .strict(),
    checks: z.array(z
        .object({
        field: NonEmptyStringSchema,
        required: z.boolean(),
        state: z.enum(["present", "defaulted", "missing", "not_applicable"]),
        sourceRefs: z.array(z.object({ type: NonEmptyStringSchema, id: NonEmptyStringSchema }).strict()),
        authorisingLedgerEntryId: NonEmptyStringSchema.nullable()
    })
        .strict()),
    issueIds: z.array(NonEmptyStringSchema),
    readinessImpact: z.enum(["none", "advisory", "blocking"]),
    href: NonEmptyStringSchema
})
    .strict();
const downstreamCounts = z
    .object({
    complete: CountSchema,
    review: CountSchema,
    incomplete: CountSchema,
    notApplicable: CountSchema
})
    .strict();
export const DownstreamReadinessRollupV1Schema = z
    .object({
    complete: CountSchema,
    review: CountSchema,
    incomplete: CountSchema,
    notApplicable: CountSchema,
    byRowType: z
        .object({
        product: downstreamCounts,
        mapping: downstreamCounts,
        panel: downstreamCounts
    })
        .strict(),
    rows: z.array(DownstreamRowConfidenceV1Schema)
})
    .strict();
export const PROJECT_PROGRESS_STEP_IDS = [
    "inputs",
    "requirements",
    "spec",
    "review",
    "scope",
    "export"
];
export const ProjectProgressV1Schema = z
    .object({
    completedWorkUnits: CountSchema,
    totalWorkUnits: CountSchema,
    percent: z.number().min(0).max(100),
    steps: z.array(z
        .object({
        id: z.enum(PROJECT_PROGRESS_STEP_IDS),
        label: NonEmptyStringSchema,
        status: z.enum(["not_started", "in_progress", "blocked", "ready", "done"]),
        completedUnits: CountSchema,
        totalUnits: CountSchema,
        blockerIds: z.array(NonEmptyStringSchema),
        href: NonEmptyStringSchema
    })
        .strict()
        .superRefine((step, context) => {
        if (step.completedUnits > step.totalUnits) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Completed step units cannot exceed total units."
            });
        }
    }))
})
    .strict()
    .superRefine((progress, context) => {
    if (progress.completedWorkUnits > progress.totalWorkUnits) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Completed work units cannot exceed total work units."
        });
    }
    const ids = progress.steps.map((step) => step.id);
    if (progress.steps.length !== PROJECT_PROGRESS_STEP_IDS.length ||
        new Set(ids).size !== PROJECT_PROGRESS_STEP_IDS.length ||
        PROJECT_PROGRESS_STEP_IDS.some((id) => !ids.includes(id))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["steps"],
            message: "Progress must contain each canonical work step exactly once."
        });
    }
});
export const ReadinessGateV1Schema = z
    .object({
    class: BlockingClassSchema,
    policy: GatePolicySchema,
    mandatory: z.boolean(),
    status: z.enum(["satisfied", "blocked", "unknown"]),
    detectedCount: CountSchema.nullable(),
    openCount: CountSchema.nullable(),
    waivedCount: CountSchema.nullable(),
    resolvedCount: CountSchema.nullable(),
    blockerIds: z.array(NonEmptyStringSchema),
    reasonCodes: z.array(NonEmptyStringSchema),
    href: NonEmptyStringSchema
})
    .strict()
    .superRefine((gate, context) => {
    const counts = [
        gate.detectedCount,
        gate.openCount,
        gate.waivedCount,
        gate.resolvedCount
    ];
    if (gate.status === "unknown" && counts.some((count) => count !== null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "An unknown readiness gate must not publish numeric counts."
        });
    }
    if (gate.status !== "unknown" && counts.some((count) => count === null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "A known readiness gate must publish numeric counts."
        });
    }
});
export const ReadinessBlockerV1Schema = z
    .object({
    id: NonEmptyStringSchema,
    primaryClass: BlockingClassSchema,
    secondaryTags: z.array(NonEmptyStringSchema),
    ledgerEntryId: NonEmptyStringSchema,
    ledgerEntryRevision: z.number().int().positive().optional(),
    sourceRef: z
        .object({ type: NonEmptyStringSchema, id: NonEmptyStringSchema })
        .strict(),
    title: NonEmptyStringSchema,
    detail: z.string().nullable(),
    status: z.enum(["open", "waived"]),
    audience: DecisionAudienceSchema,
    canWaive: z.boolean(),
    waiver: LedgerWaiverV1Schema.nullable(),
    href: NonEmptyStringSchema,
    detectedAt: TimestampSchema,
    lastSeenAt: TimestampSchema
})
    .strict();
const byClassCountSchema = z
    .object({
    countStatus: z.enum(["known", "unknown"]),
    detected: CountSchema.nullable(),
    open: CountSchema.nullable(),
    waived: CountSchema.nullable(),
    resolved: CountSchema.nullable()
})
    .strict()
    .superRefine((counts, context) => {
    const values = [counts.detected, counts.open, counts.waived, counts.resolved];
    if (counts.countStatus === "unknown" && values.some((count) => count !== null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Unknown class counts must remain null."
        });
    }
    if (counts.countStatus === "known" && values.some((count) => count === null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Known class counts must be numeric."
        });
    }
});
const byClassCountsShape = Object.fromEntries(BLOCKING_CLASSES.map((blockingClass) => [blockingClass, byClassCountSchema]));
const scheduleStateCountsShape = Object.fromEntries(LEDGER_STATES.map((state) => [state, CountSchema.nullable()]));
export const ProjectReadinessV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    orgId: NonEmptyStringSchema,
    projectId: NonEmptyStringSchema,
    revision: z.number().int().positive(),
    generatedAt: TimestampSchema,
    sourceVersion: ProjectReadinessSourceVersionV1Schema,
    fileGeneration: z
        .object({
        canGenerate: z.boolean().nullable(),
        status: z.enum(["available", "unavailable", "unknown"]),
        allowedModes: z.array(z.enum(["internal_draft", "client_issue"])),
        formats: z.array(NonEmptyStringSchema),
        reasonCodes: z.array(NonEmptyStringSchema)
    })
        .strict(),
    clientIssue: z
        .object({
        readyToExport: z.boolean(),
        status: z.enum(["ready", "blocked", "unknown"]),
        mandatoryOpenCount: CountSchema.nullable(),
        mandatoryWaivedCount: CountSchema.nullable(),
        advisoryOpenCount: CountSchema.nullable(),
        reasonCodes: z.array(NonEmptyStringSchema)
    })
        .strict(),
    counts: z
        .object({
        complete: z.boolean(),
        uniqueOpenBlockers: CountSchema.nullable(),
        uniqueWaivedBlockers: CountSchema.nullable(),
        decisionsOpen: CountSchema.nullable(),
        clientRfisOpen: CountSchema.nullable(),
        scheduleRowsByState: z.object(scheduleStateCountsShape).strict(),
        byClass: z.object(byClassCountsShape).strict()
    })
        .strict(),
    gates: z.array(ReadinessGateV1Schema),
    blockers: z.array(ReadinessBlockerV1Schema),
    progress: ProjectProgressV1Schema,
    downstream: DownstreamReadinessRollupV1Schema
})
    .strict()
    .superRefine((snapshot, context) => {
    const expectedGenerationStatus = snapshot.fileGeneration.canGenerate === null
        ? "unknown"
        : snapshot.fileGeneration.canGenerate
            ? "available"
            : "unavailable";
    if (snapshot.fileGeneration.status !== expectedGenerationStatus) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["fileGeneration", "status"],
            message: "File generation status must match its nullable capability."
        });
    }
    if (snapshot.clientIssue.readyToExport !==
        (snapshot.clientIssue.status === "ready")) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["clientIssue", "readyToExport"],
            message: "Client issue readiness must agree with client issue status."
        });
    }
    if (snapshot.fileGeneration.allowedModes.includes("client_issue") &&
        (snapshot.fileGeneration.canGenerate !== true ||
            !snapshot.clientIssue.readyToExport)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["fileGeneration", "allowedModes"],
            message: "Client issue mode requires both renderer capability and issue readiness."
        });
    }
    const gateClasses = snapshot.gates.map((gate) => gate.class);
    if (snapshot.gates.length !== BLOCKING_CLASSES.length ||
        new Set(gateClasses).size !== BLOCKING_CLASSES.length ||
        BLOCKING_CLASSES.some((blockingClass) => !gateClasses.includes(blockingClass))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["gates"],
            message: "Readiness must contain each canonical gate exactly once."
        });
    }
    const mandatoryUnknown = snapshot.gates.some((gate) => gate.mandatory && gate.status === "unknown");
    if (mandatoryUnknown) {
        if (snapshot.counts.complete ||
            snapshot.clientIssue.status !== "unknown" ||
            snapshot.counts.uniqueOpenBlockers !== null ||
            snapshot.clientIssue.mandatoryOpenCount !== null) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "An unknown mandatory gate must fail closed with unknown aggregates."
            });
        }
    }
    const blockerIds = snapshot.blockers.map((blocker) => blocker.id);
    if (new Set(blockerIds).size !== blockerIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["blockers"],
            message: "Readiness blockers must be deduplicated by canonical ID."
        });
    }
});
export const ProjectLedgerSummaryV1Schema = z
    .object({
    revision: z.number().int().nonnegative(),
    countsByState: z
        .object(Object.fromEntries(LEDGER_STATES.map((state) => [state, CountSchema])))
        .strict(),
    countsByAudience: z
        .object(Object.fromEntries(DECISION_AUDIENCES.map((audience) => [audience, CountSchema])))
        .strict()
})
    .strict();
export const GenerationZeroChangeReasonV1Schema = z
    .object({
    code: z.enum([
        "no_source_facts",
        "all_candidates_duplicate",
        "no_candidate_met_policy",
        "source_stale",
        "candidates_rejected"
    ]),
    explanation: NonEmptyStringSchema
})
    .strict();
export const GenerationResultV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    operationId: NonEmptyStringSchema,
    source: NonEmptyStringSchema,
    sourceVersionHash: Sha256Schema,
    candidateCount: CountSchema,
    createdCount: CountSchema,
    updatedCount: CountSchema,
    deduplicatedCount: CountSchema,
    rejectedCount: CountSchema,
    zeroChangeReason: GenerationZeroChangeReasonV1Schema.nullable(),
    changed: z.array(z.unknown()),
    rejected: z.array(z
        .object({
        sourceId: NonEmptyStringSchema,
        code: NonEmptyStringSchema,
        explanation: NonEmptyStringSchema
    })
        .strict()),
    readiness: ProjectReadinessV1Schema
})
    .strict()
    .superRefine((result, context) => {
    const changedCount = result.createdCount + result.updatedCount;
    if (changedCount === 0 && result.zeroChangeReason === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["zeroChangeReason"],
            message: "A zero-change generation result requires an exact reason."
        });
    }
    if (changedCount > 0 && result.zeroChangeReason !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["zeroChangeReason"],
            message: "A changed generation result cannot have a zero-change reason."
        });
    }
});
export const FAIL_CLOSED_READINESS_POLICY_PROFILE_V1 = ReadinessPolicyProfileV1Schema.parse({
    schemaVersion: 1,
    id: "fail-closed-default",
    version: "1",
    issueProfile: "internal-policy-unresolved",
    gates: Object.fromEntries(BLOCKING_CLASSES.map((blockingClass) => [
        blockingClass,
        {
            mandatory: true,
            policy: "hard",
            allowedResolutionOutcomes: [...LEDGER_RESOLUTION_OUTCOMES],
            waiverRoleIds: []
        }
    ])),
    downstreamRequiredChecksVersion: "unresolved-fail-closed-v1",
    progressWorkUnitsVersion: "unresolved-work-facts-v1",
    allowBlockedInternalDraft: false
});
const answeredPolicyGate = (policy) => ({
    mandatory: policy !== "advisory",
    policy,
    allowedResolutionOutcomes: [...LEDGER_RESOLUTION_OUTCOMES],
    waiverRoleIds: policy === "waivable" ? ["estimator"] : []
});
const answeredPolicyGates = (pricePolicy) => ({
    uncertified_drafts: answeredPolicyGate("hard"),
    unresolved_user_decisions: answeredPolicyGate("waivable"),
    unanswered_rfis: answeredPolicyGate("hard"),
    unapproved_assumptions: answeredPolicyGate("hard"),
    unchecked_takeoff_anomalies: answeredPolicyGate("waivable"),
    missing_catalogue_prices: answeredPolicyGate(pricePolicy),
    failed_document_reads: answeredPolicyGate("hard"),
    stale_generation: answeredPolicyGate("hard"),
    unreported_ai_usage: answeredPolicyGate("advisory"),
    unresolved_quote_lines: answeredPolicyGate("hard"),
    incomplete_downstream_rows: answeredPolicyGate("waivable")
});
export const PRICED_CLIENT_ISSUE_READINESS_POLICY_V1 = ReadinessPolicyProfileV1Schema.parse({
    schemaVersion: 1,
    id: "rich-answered-priced-client-issue",
    version: "2026-07-20",
    issueProfile: "priced",
    gates: answeredPolicyGates("hard"),
    downstreamRequiredChecksVersion: "room-controller-terminal-power-v1",
    progressWorkUnitsVersion: "even-six-stage-export-event-v1",
    allowBlockedInternalDraft: true
});
export const TECHNICAL_CLIENT_ISSUE_READINESS_POLICY_V1 = ReadinessPolicyProfileV1Schema.parse({
    schemaVersion: 1,
    id: "rich-answered-technical-client-issue",
    version: "2026-07-20",
    issueProfile: "technical_only",
    gates: answeredPolicyGates("waivable"),
    downstreamRequiredChecksVersion: "room-controller-terminal-power-v1",
    progressWorkUnitsVersion: "even-six-stage-export-event-v1",
    allowBlockedInternalDraft: true
});
export const readinessPolicyForIssueProfile = (issueProfile) => issueProfile === "technical_only"
    ? TECHNICAL_CLIENT_ISSUE_READINESS_POLICY_V1
    : PRICED_CLIENT_ISSUE_READINESS_POLICY_V1;
//# sourceMappingURL=readiness.js.map